import { Link } from 'react-router-dom';
import { useCompanyBranding, useCompanyContact } from '../../hooks/unified';
import { useSEO, SEO_CONFIGS } from '../../hooks/useSEO';

export function TermsOfServicePage() {
  useSEO(SEO_CONFIGS.terms);
  const branding = useCompanyBranding();
  const contact = useCompanyContact();
  const lastUpdated = 'November 27, 2025';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-gray-300">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Welcome to {branding.name}. By accessing or using our website and services, you agree to be bound 
                by these Terms of Service. Please read them carefully before using our platform.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600 mb-6">
                By accessing or using {branding.name}'s website and services, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms of Service and our Privacy Policy. If you do not 
                agree to these terms, please do not use our services.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Description of Services</h2>
              <p className="text-gray-600 mb-4">
                {branding.name} provides an online platform that connects job seekers with employers. Our services include:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                <li>Job listing and search functionality</li>
                <li>Resume/CV upload and management</li>
                <li>Job application submission</li>
                <li>Communication tools between candidates and employers</li>
                <li>Career resources and information</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Account Registration</h3>
              <p className="text-gray-600 mb-4">
                To access certain features of our platform, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Account Termination</h3>
              <p className="text-gray-600 mb-6">
                We reserve the right to suspend or terminate your account at any time for violations of these 
                terms or for any other reason at our sole discretion. You may also delete your account at any time.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. User Conduct</h2>
              <p className="text-gray-600 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                <li>Provide false or misleading information</li>
                <li>Impersonate any person or entity</li>
                <li>Use the platform for any illegal purpose</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Upload malicious software or content</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Scrape or collect data without permission</li>
                <li>Interfere with the proper functioning of the platform</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Content and Intellectual Property</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Your Content</h3>
              <p className="text-gray-600 mb-6">
                You retain ownership of content you submit (resumes, applications, etc.). By submitting content, 
                you grant us a non-exclusive, worldwide license to use, display, and distribute your content 
                in connection with our services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Our Content</h3>
              <p className="text-gray-600 mb-6">
                All content on our platform, including text, graphics, logos, and software, is owned by 
                {branding.name} or our licensors and is protected by intellectual property laws. You may not 
                copy, modify, or distribute our content without permission.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Job Listings and Applications</h2>
              <p className="text-gray-600 mb-4">
                We strive to provide accurate job listings, but we do not guarantee:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                <li>The accuracy or completeness of job listings</li>
                <li>That you will receive responses to applications</li>
                <li>Employment outcomes or job offers</li>
                <li>The conduct or reliability of employers</li>
              </ul>
              <p className="text-gray-600 mb-6">
                You are responsible for verifying job details and conducting your own due diligence before 
                accepting any employment offer.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Disclaimer of Warranties</h2>
              <p className="text-gray-600 mb-6">
                Our services are provided "as is" and "as available" without warranties of any kind, either 
                express or implied. We do not warrant that our services will be uninterrupted, error-free, 
                or secure. We disclaim all warranties, including implied warranties of merchantability, 
                fitness for a particular purpose, and non-infringement.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-600 mb-6">
                To the maximum extent permitted by law, {branding.name} shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages, including loss of profits, data, 
                or other intangible losses, resulting from your use of our services.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Indemnification</h2>
              <p className="text-gray-600 mb-6">
                You agree to indemnify and hold harmless {branding.name}, its officers, directors, employees, 
                and agents from any claims, damages, losses, or expenses arising from your use of our services 
                or violation of these terms.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Modifications to Terms</h2>
              <p className="text-gray-600 mb-6">
                We reserve the right to modify these Terms of Service at any time. We will notify users of 
                significant changes by posting a notice on our website or sending an email. Your continued 
                use of our services after changes constitutes acceptance of the modified terms.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Governing Law</h2>
              <p className="text-gray-600 mb-6">
                These Terms of Service shall be governed by and construed in accordance with applicable laws, 
                without regard to conflict of law principles. Any disputes arising from these terms shall be 
                resolved in the appropriate courts.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Contact Information</h2>
              <p className="text-gray-600 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <p className="text-gray-700 font-semibold">{branding.name}</p>
                {contact.address && <p className="text-gray-600">{contact.address}</p>}
                {contact.email && (
                  <p className="text-gray-600">
                    Email: <a href={`mailto:${contact.email}`} className="text-primary-600 hover:text-primary-700">{contact.email}</a>
                  </p>
                )}
                {contact.phone && (
                  <p className="text-gray-600">
                    Phone: <a href={`tel:${contact.phone}`} className="text-primary-600 hover:text-primary-700">{contact.phone}</a>
                  </p>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
                View Privacy Policy →
              </Link>
              <Link to="/contact" className="text-primary-600 hover:text-primary-700 font-medium">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
